package vegasful.admin.loaders

import androidx.compose.runtime.*
import kotlinx.coroutines.delay
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text
import vegasful.admin.Application
import vegasful.admin.admin
import vegasful.admin.api.LogQueryInput
import vegasful.admin.api.VenueUpdateInput
import vegasful.admin.api.client.AdminLoader
import vegasful.admin.components.box
import vegasful.admin.components.boxMessage
import vegasful.admin.components.button
import vegasful.admin.components.entityView
import vegasful.admin.components.table
import vegasful.admin.views.contentContainer
import vegasful.admin.views.venuesSearch

// Insights query that finds venue aliases loaders reported as unmatched.
private val MISSING_VENUE_QUERY = """
    filter `detail-type` = "data-provider-missing-venue"
    | fields @timestamp, detail.loader, detail.alias, detail.id
    | sort @timestamp desc
    | limit 1000
""".trimIndent()

private data class MissingAlias(val loader: String?, val alias: String?, val id: String?)

@Composable
fun loaders() {
    var loaders by remember { mutableStateOf(emptyList<AdminLoader>()) }
    var missing by remember { mutableStateOf(emptyList<MissingAlias>()) }

    // the missing-alias entry the user is currently connecting to a venue
    var connecting by remember { mutableStateOf<MissingAlias?>(null) }

    LaunchedEffect(true) {
        Application.api.admin("ReadLoaders") {
            loaders {
                loaders {
                    id
                    name
                    schedule
                }
            }
        }.loaders.loaders.let {
            loaders = it.sortedWith(compareBy(String.CASE_INSENSITIVE_ORDER) { it.name })
        }

        // Run the missing-venue Insights query via the generic logs API: start it,
        // then poll for results. Wrapped so a failure here does not prevent the
        // loaders list from rendering.
        try {
            val queryId = Application.api.admin("StartLogQuery") {
                logs {
                    startQuery(LogQueryInput(query = MISSING_VENUE_QUERY))
                }
            }.logs.startQuery

            var attempts = 0
            var done = false
            while (!done && attempts < 40) {
                val result = Application.api.admin("LogQueryResults") {
                    logs {
                        results(queryId) {
                            status
                            rows {
                                fields {
                                    field
                                    value
                                }
                            }
                        }
                    }
                }.logs.results

                when (result.status) {
                    "Complete" -> {
                        missing = result.rows.map { row ->
                            val byName = row.fields.associate { it.field to it.value }
                            MissingAlias(byName["detail.loader"], byName["detail.alias"], byName["detail.id"])
                        }.distinct()
                        done = true
                    }

                    "Failed", "Cancelled", "Timeout" -> {
                        Application.notifications.showError("Missing-alias query ${result.status}.")
                        done = true
                    }

                    else -> {
                        attempts++
                        delay(500)
                    }
                }
            }
        } catch (t: Throwable) {
            Application.notifications.showError(t.message ?: "Unable to load missing aliases.")
        }
    }

    // Connect the aliases reported in a missing-venue entry to the selected venue.
    suspend fun connect(entry: MissingAlias, venueId: String) {
        val toAdd = entry.alias
            ?.split(",")
            ?.map { it.trim() }
            ?.filter { it.isNotEmpty() }
            ?.ifEmpty { listOfNotNull(entry.id) }
            ?: listOfNotNull(entry.id)

        if (toAdd.isEmpty()) {
            Application.notifications.showError("There is no alias to connect for this entry.")
            return
        }

        try {
            // Read the venue's existing aliases so we add to them rather than replace.
            val existing = Application.api.admin("ReadVenueAliases") {
                venues {
                    venue(venueId) {
                        local {
                            aliases
                        }
                    }
                }
            }.venues.venue.local.aliases.orEmpty()

            Application.api.admin("AddVenueAlias") {
                venues {
                    venue(venueId) {
                        local {
                            update(VenueUpdateInput(aliases = (existing + toAdd).distinct())) {
                                aliases
                            }
                        }
                    }
                }
            }

            missing = missing - entry
            connecting = null
        } catch (t: Throwable) {
            Application.notifications.showError(t.message ?: "Failed to connect the alias.")
        }
    }

    contentContainer {
        entityView {
            title = "Loaders"

            action {
                title = "Add"

                action {
                    Application.navigation.navigate("content/loaders/add")
                }
            }

            content {
                box({
                    paddedContent = false
                }) {
                    table<AdminLoader> {
                        items(loaders)

                        column("Name") {
                            content {
                                A(href = "#content/loaders/${it.id}") {
                                    Text(it.name)
                                }
                            }
                        }

                        column("Schedule") {
                            content {
                                Text(it.schedule ?: "")
                            }
                        }
                    }
                }

                box({
                    title = "Missing Aliases (last 24h)"
                    paddedContent = false
                }) {
                    if (missing.isEmpty()) {
                        boxMessage("No missing venue aliases were reported in the last 24 hours.")
                    } else {
                        table<MissingAlias> {
                            items(missing)

                            column("Loader") {
                                content {
                                    Text(it.loader ?: "")
                                }
                            }

                            column("Alias") {
                                content {
                                    Text(it.alias ?: "")
                                }
                            }

                            column("ID") {
                                content {
                                    Text(it.id ?: "")
                                }
                            }

                            column {
                                content { entry ->
                                    button("Connect") {
                                        connecting = entry
                                    }
                                }
                            }
                        }
                    }
                }

                connecting?.let { entry ->
                    box({
                        title = "Connect Alias"
                    }) {
                        Div {
                            Text(
                                "Connect ${entry.alias ?: entry.id ?: ""}" +
                                        (entry.loader?.let { " (from $it)" } ?: "") +
                                        " to a venue:"
                            )
                        }

                        venuesSearch({ true }, "Venue Search:") { venue ->
                            connect(entry, venue.id)
                        }

                        button("Cancel") {
                            connecting = null
                        }
                    }
                }
            }
        }
    }
}
